import { getCookie } from '@api/HelperAPI';
import { datadogLogs } from '@datadog/browser-logs';
import {
    GetFormalOffersResponse,
    fetchGetApplication,
    fetchGetFormalOffers,
    fetchGetProductOffers,
    fetchGetStateByStateKey,
    GetContextResponse,
    fetchPutState,
    PutStateRequestBody,
    fetchGetSplitTest,
    fetchGetUserConsent,
    fetchGetDrawDownRepaymentScheduleCreditFacility
} from '@iwoca/lapi-client/edge';
import { useMutation, useQuery } from '@tanstack/react-query';

import { TGetContext } from './lapi.types';
import { mapOfferDurationToMonths } from './mapOfferDurationToMonths';
import { getAccountsQueryConfig } from './queryConfigs';
import { SITE_CONTEXT_CACHE_KEY } from '../../iwQueryClient/iwQueryClient';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { TSplitTestBranch } from '../../myAccount/multiLoanDashboard/AccountFeatureEvaluator/AccountFeatureEvaluator';

interface QueryOptions {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    refetchInterval?: false | number | ((data: any) => number | false);
    enabled?: boolean;
}

const sortFormalOffersByDuration = (
    offers:
        | NonNullable<GetFormalOffersResponse['data']>['formal_offers']
        | undefined
) => {
    if (!offers) return [];

    return offers.sort((a, b) => {
        const aMonths = mapOfferDurationToMonths(a);
        const bMonths = mapOfferDurationToMonths(b);

        return aMonths - bMonths;
    });
};

export function useGetFormalOffers({
    enabled = true
}: { enabled?: boolean } = {}) {
    const stateKey = useStateKey();

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['fetchGetFormalOffers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        enabled
    });

    return {
        formalOffers:
            sortFormalOffersByDuration(data?.data?.formal_offers) || null,
        loadingFormalOffers: isLoading && enabled,
        formalOffersError: isError,
        fetchFormalOffers: refetch
    };
}

export function useGetFormalOffer(offerId: string | undefined) {
    const stateKey = useStateKey();

    const enabled = Boolean(offerId);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['fetchGetFormalOffers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        enabled: enabled,
        select: (data) =>
            data.data?.formal_offers.find(
                (formal_offer) => formal_offer.offer_id === offerId
            )
    });

    return {
        formalOffer: data || null,
        loadingFormalOffer: isLoading && Boolean(enabled),
        formalOfferError: isError
    };
}

// Please keep this list up to date with currently used split tests in Pandora.
// Add any new split tests as required.
export type TSplitTestNames =
    | 'uni_approved_view_test_2023'
    | 'no_pg_test_2025'
    | 'non_applicant_pg_consent'
    | 'credit_compass_test'
    | 'self_serve_account_pages'
    | 'de_repayment_reduction_test_2025'
    | 'uk_dynamic_dashboard_2026'
    | 'cc_waitlist_interstitial'
    | 'cc_cross_sell_offer_enabled'
    | 'self_serve_associated_people_check_2026'
    | 'new_customer_funding_requirements_q2_2026';

type splitTests = {
    data: {
        user_split_tests: { [k: string]: boolean }[];
    };
};

const truthyValues: (string | boolean)[] = ['True', 'true', true];

/**
 * Prefer `useSplitTestBranch` for new code. It is more explicit and can assign
 * a branch on demand via the `/edge/split_test` endpoint, whereas
 * `useSplitTests` only reads tests already exposed in `/edge/context` (a
 * grab-bag we're moving away from) and only picks up tests the customer is
 * already assigned to.
 */
export function useSplitTests(splitTestName: TSplitTestNames): boolean {
    const { data: splitTests } = useQuery({
        queryKey: [SITE_CONTEXT_CACHE_KEY],
        select: (data: splitTests) => data.data.user_split_tests
    });

    const cookieValue = getCookie(`split_test_${splitTestName}`);
    if (cookieValue) {
        return truthyValues.includes(cookieValue);
    }

    const splitTestsAsObj = splitTests!.reduce((acc, obj) => {
        const [key, val] = Object.entries(obj)[0];

        return { ...acc, [key]: val };
    }, {});

    return splitTestsAsObj[splitTestName];
}

export function useGetProductOffers(stateKey: string) {
    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ['product_offers', stateKey],
        queryFn: () => fetchGetProductOffers({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError, refetch, isFetching };
}

export function useGetApplication(stateKey: string) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError };
}

export function useGetCustomerState(stateKey: string) {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError, refetch };
}

export const useGetDocuments = (stateKey: string) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['documents', stateKey],
        queryFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/document_upload/${stateKey}/`
            );
            const data = response.json();
            return data;
        }
    });
    return { data, isLoading, isError };
};

export function useGetAccounts(queryOptions?: QueryOptions) {
    const stateKey = useStateKey();

    return useQuery({
        ...getAccountsQueryConfig(stateKey),
        enabled: queryOptions?.enabled ?? true,
        select: (data) => data.data!
    });
}

export function useGetContext() {
    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: [SITE_CONTEXT_CACHE_KEY],
        select: (data: GetContextResponse) => data.data
    });

    return {
        data: data as TGetContext['data'],
        isLoading,
        isError,
        refetch,
        isFetching
    };
}

export function usePutStateMutation(stateKey: string) {
    const { mutateAsync, isError, isLoading } = useMutation({
        mutationFn: async ({ body }: { body: PutStateRequestBody }) => {
            await fetchPutState({
                stateKey,
                body
            });
        },
        onError: (error, variables) => {
            if (error instanceof Error) {
                datadogLogs.logger.error(
                    `Fetch PUT /state/${stateKey}/ error`,
                    {
                        requestBody: variables,
                        error: error.message
                    }
                );
            }
        }
    });

    return {
        mutatePutState: mutateAsync,
        isLoading,
        isError
    };
}

/**
 * Preferred hook for reading split test branches (over `useSplitTests`).
 * Uses the `/edge/split_test/{state_key}/{test_name}` endpoint, which returns
 * the customer's branch or assigns one on demand. Works for both toggles
 * (include/exclude) and multi-branch tests.
 */
export function useSplitTestBranch(args: {
    test: string;
    suspense: false;
    cookieOnly?: boolean;
    enabled?: boolean;
}): { branch: string | undefined; isLoading: boolean };
export function useSplitTestBranch(args: {
    test: string;
    suspense?: true;
    cookieOnly?: boolean;
    enabled?: boolean;
}): { branch: string; isLoading: boolean };
export function useSplitTestBranch({
    test,
    suspense = true,
    cookieOnly = false,
    enabled = true
}: {
    test: string;
    suspense?: boolean;
    cookieOnly?: boolean;
    enabled?: boolean;
}) {
    const stateKey = useStateKey();

    // Check cookie
    const cookieBranch = getCookie(`split_test_${test}`);

    const shouldFetch = !cookieOnly && !cookieBranch && enabled;

    const { data: branchFromApi, isLoading } = useQuery({
        queryKey: ['fetchGetSplitTest', stateKey, test],
        // A failed split-test fetch falls back to 'EXCLUDE' rather than
        // erroring, so a flaky experiment lookup never breaks the consuming
        // component (which may be rendered inside a Suspense/ErrorBoundary).
        queryFn: () =>
            fetchGetSplitTest({ stateKey, experimentName: test }).catch(() => ({
                data: { branch: 'EXCLUDE' }
            })),
        select: ({ data }) => data.branch,
        enabled: shouldFetch,
        suspense
    });

    return {
        branch: cookieBranch || (branchFromApi as TSplitTestBranch),
        isLoading: shouldFetch && isLoading
    };
}

export const useGetUserConsent = () => {
    const stateKey = useStateKey();

    const { data } = useQuery({
        queryKey: ['fetchGetUserConsent', stateKey],
        queryFn: () =>
            fetchGetUserConsent({
                stateKey: stateKey as string,
                provider: 'truelayer_open_banking'
            }),
        select: (data) => data.data
    });

    return {
        userConsent: data?.consent_active ?? false
    };
};

export const useDrawdownRepaymentSchedule = ({
    offerId,
    drawdownAmount,
    earlyFinalRepaymentDate,
    enabled = true,
    suspense = false
}: {
    offerId: string;
    drawdownAmount: number;
    earlyFinalRepaymentDate?: string | null;
    enabled?: boolean;
    suspense?: boolean;
}) => {
    const stateKey = useStateKey();

    return useQuery({
        queryKey: [
            'drawdownRepaymentSchedule',
            offerId,
            stateKey,
            drawdownAmount,
            earlyFinalRepaymentDate
        ],
        queryFn: () =>
            fetchGetDrawDownRepaymentScheduleCreditFacility({
                stateKey,
                offerId,
                query: {
                    amount: drawdownAmount,
                    ...(earlyFinalRepaymentDate && {
                        early_final_repayment_date: earlyFinalRepaymentDate
                    })
                }
            }),
        enabled: Boolean(offerId) && enabled,
        select: (data) => data.data,
        suspense: suspense
    });
};
